import type { MouseEvent, ReactNode, SyntheticEvent } from "react";
import { useCallback, useEffect, useRef } from "react";

export function Dialog({
  open,
  onClose,
  className,
  labelledBy,
  returnFocus,
  dismissible = true,
  children,
}: {
  open: boolean;
  onClose: () => void;
  className: string;
  labelledBy: string;
  returnFocus?: HTMLElement | null | undefined;
  dismissible?: boolean;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const invokerRef = useRef<HTMLElement | null>(null);
  const lastFocusedOutsideRef = useRef<HTMLElement | null>(null);
  useEffect(() => {
    const rememberOutsideFocus = (event: FocusEvent) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (ref.current?.contains(target)) return;
      lastFocusedOutsideRef.current = target;
    };
    document.addEventListener("focusin", rememberOutsideFocus);
    return () => {
      document.removeEventListener("focusin", rememberOutsideFocus);
    };
  }, []);
  const restoreInvoker = useCallback(() => {
    setTimeout(() => {
      const invoker = invokerRef.current;
      if (!invoker?.isConnected) return;
      invoker.focus();
    }, 0);
  }, []);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open) {
      openDialog(
        dialog,
        returnFocus,
        lastFocusedOutsideRef.current,
        invokerRef,
      );
      return;
    }
    closeDialog(dialog, restoreInvoker);
  }, [open, restoreInvoker, returnFocus]);
  const backdrop = (event: MouseEvent<HTMLDialogElement>) => {
    if (dismissible && event.target === event.currentTarget) onClose();
  };
  const cancel = (event: SyntheticEvent<HTMLDialogElement>) => {
    if (!dismissible) event.preventDefault();
  };
  const close = () => {
    if (open) onClose();
    restoreInvoker();
  };
  return (
    <dialog
      ref={ref}
      className={className}
      aria-labelledby={labelledBy}
      onClose={close}
      onCancel={cancel}
      onClick={backdrop}
    >
      {children}
    </dialog>
  );
}

function focusableOutsideDialog(
  target: Element | null,
  dialog: HTMLDialogElement,
): HTMLElement | null {
  if (!(target instanceof HTMLElement)) return null;
  return dialog.contains(target) ? null : target;
}

function openDialog(
  dialog: HTMLDialogElement,
  returnFocus: HTMLElement | null | undefined,
  lastFocusedOutside: HTMLElement | null,
  invokerRef: { current: HTMLElement | null },
): void {
  if (dialog.open) return;
  invokerRef.current = resolveReturnFocus(
    returnFocus,
    document.activeElement,
    dialog,
    lastFocusedOutside,
  );
  dialog.showModal();
}

function resolveReturnFocus(
  returnFocus: HTMLElement | null | undefined,
  active: Element | null,
  dialog: HTMLDialogElement,
  lastFocusedOutside: HTMLElement | null,
): HTMLElement | null {
  if (returnFocus) return returnFocus;
  return focusableOutsideDialog(active, dialog) ?? lastFocusedOutside;
}

function closeDialog(
  dialog: HTMLDialogElement,
  restoreInvoker: () => void,
): void {
  if (!dialog.open) return;
  dialog.close();
  restoreInvoker();
}
